import React, { useState } from 'react';
import { auth, database, storage } from '../../firebaseConfig';
import { ref as dbRef, push, set } from 'firebase/database';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './AddCategory.css';

function AddCategory({ onCategoryAdded, onCancel }) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        tier: 'team'
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image file size must be less than 5MB');
                return;
            }

            setImageFile(file);
            setError('');

            // Create preview
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (file) => {
        if (!auth.currentUser || !file) return null;

        const fileName = `academy/categories/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        try {
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading image:', error);
            throw new Error('Failed to upload image');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!auth.currentUser) {
            setError('You must be logged in to create a category');
            return;
        }

        if (!formData.name.trim()) {
            setError('Category name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let imageUrl = null;
            
            // Upload image if provided
            if (imageFile) {
                imageUrl = await uploadImage(imageFile);
            }

            const categoryData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                tier: formData.tier,
                imageUrl: imageUrl,
                createdBy: auth.currentUser.email,
                createdByUserId: auth.currentUser.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Push to public Academy path
            const categoriesRef = dbRef(database, 'Academy/categories');
            const newCategoryRef = push(categoriesRef);
            await set(newCategoryRef, categoryData);

            // Create the category object with the generated ID
            const newCategory = {
                id: newCategoryRef.key,
                ...categoryData
            };

            // Call the callback with the new category
            onCategoryAdded(newCategory);

        } catch (error) {
            console.error('Error creating category:', error);
            setError('Failed to create category. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    return (
        <div className="add-category-container">
            <div className="add-category-header">
                <h3>Add New Learning Category</h3>
                <p>Create a new category to organize your learning videos</p>
            </div>

            <form onSubmit={handleSubmit} className="add-category-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="name">Category Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Customer Acquisition, Sales Techniques..."
                        maxLength={50}
                        required
                    />
                    <small className="char-count">{formData.name.length}/50</small>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe what this category covers..."
                        rows={3}
                        maxLength={200}
                    />
                    <small className="char-count">{formData.description.length}/200</small>
                </div>

                <div className="form-group">
                    <label>Access Tier *</label>
                    <div className="academy-tier-selector">
                        {['team', 'standard', 'premium'].map(tier => (
                            <button
                                key={tier}
                                type="button"
                                className={`academy-tier-btn academy-tier-${tier} ${formData.tier === tier ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, tier }))}
                            >
                                {tier.charAt(0).toUpperCase() + tier.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="image">Category Image</label>
                    <div className="image-upload-area">
                        {imagePreview ? (
                            <div className="image-preview">
                                <img src={imagePreview} alt="Category preview" />
                                <button 
                                    type="button" 
                                    className="remove-image-btn"
                                    onClick={removeImage}
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <div className="image-upload-placeholder">
                                <div className="upload-icon">📁</div>
                                <p>Choose an image for this category</p>
                                <small>JPG, PNG up to 5MB</small>
                            </div>
                        )}
                        <input
                            type="file"
                            id="image"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="image-input"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button 
                        type="button" 
                        onClick={onCancel}
                        className="cancel-btn"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className="submit-btn"
                        disabled={loading || !formData.name.trim()}
                    >
                        {loading ? 'Creating...' : 'Create Category'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default AddCategory;
